import { Hash, Headphones, Plus, Radio, Search, Video } from 'lucide-react';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import type { ConversationSummary } from '../types';
import { formatTimestamp, relativeLastSeen } from '../lib/format';
import { getConversationAvatar, getConversationTitle, getOtherMember } from '../lib/conversations';
import Avatar from './Avatar';

export default function ConversationList({
  conversations,
  selectedConversationId,
  currentUserId,
  onlineIds,
  loading,
  onSelect,
  onNewGroup,
}: {
  conversations: ConversationSummary[];
  selectedConversationId: string | null;
  currentUserId: string;
  onlineIds: Set<string>;
  loading: boolean;
  onSelect: (conversationId: string) => void;
  onNewGroup: () => void;
}) {
  const [query, setQuery] = useState('');
  const channelItems = [
    { label: 'general', icon: Hash, copy: 'Text channel' },
    { label: 'Voice Lounge', icon: Headphones, copy: 'Voice channel' },
    { label: 'Video Room', icon: Video, copy: 'Video channel' },
    { label: 'Live Updates', icon: Radio, copy: 'Announcements' },
  ];

  const filtered = useMemo(
    () =>
      conversations.filter((conversation) =>
        getConversationTitle(conversation, currentUserId).toLowerCase().includes(query.toLowerCase()),
      ),
    [conversations, currentUserId, query],
  );

  return (
    <aside className="relay-glass flex h-screen min-w-0 flex-col border-r border-white/10">
      <div className="border-b border-white/10 p-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black tracking-[-0.02em] text-white">Relay</h1>
            <p className="text-xs text-slate-500">Design Hub community</p>
          </div>
          <button
            type="button"
            onClick={onNewGroup}
            className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 transition hover:-translate-y-0.5 hover:bg-white/10 hover:text-white"
            title="Create group chat"
            aria-label="Create group chat"
          >
            <Plus size={18} />
          </button>
        </div>
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="w-full rounded-xl border border-white/10 bg-black/25 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-slate-500 transition focus:border-relay-cyan/50"
            placeholder="Search conversations"
          />
        </label>
      </div>

      <div className="relay-scrollbar flex-1 overflow-y-auto p-2">
        <section className="mb-3">
          <div className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">
            Channels
          </div>
          <div className="space-y-1">
            {channelItems.map(({ label, icon: Icon, copy }, index) => (
              <button
                key={label}
                type="button"
                className={clsx(
                  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition',
                  index === 0 ? 'bg-relay-blue/15 text-white' : 'text-slate-400 hover:bg-white/[0.045] hover:text-white',
                )}
              >
                <Icon size={16} className={index === 0 ? 'text-relay-cyan' : 'text-slate-500'} />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{label}</span>
                  <span className="text-xs text-slate-600">{copy}</span>
                </span>
              </button>
            ))}
          </div>
        </section>

        <div className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">
          Direct messages and groups
        </div>
        {loading && <p className="px-3 py-4 text-sm text-slate-500">Loading conversations...</p>}
        {!loading && filtered.length === 0 && (
          <p className="px-3 py-4 text-sm leading-6 text-slate-500">
            No conversations yet. Search for users in Friends or create a group.
          </p>
        )}
        <div className="space-y-1">
          {filtered.map((conversation) => {
            const title = getConversationTitle(conversation, currentUserId);
            const otherMember = getOtherMember(conversation, currentUserId);
            const online =
              conversation.type === 'group'
                ? conversation.members.some((member) => member.user_id !== currentUserId && onlineIds.has(member.user_id))
                : Boolean(otherMember && onlineIds.has(otherMember.id));

            return (
              <button
                key={conversation.id}
                type="button"
                onClick={() => onSelect(conversation.id)}
                className={clsx(
                  'group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition',
                  selectedConversationId === conversation.id
                    ? 'bg-relay-blue/15 text-white shadow-[inset_3px_0_0_rgba(34,211,238,.75)]'
                    : 'text-slate-300 hover:bg-white/[0.045] hover:text-white',
                )}
              >
                <Avatar src={getConversationAvatar(conversation, currentUserId)} name={title} online={online} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{title}</span>
                  <span className="mt-0.5 block truncate text-xs text-slate-500">
                    {conversation.type === 'group'
                      ? `${conversation.members.length} members`
                      : online
                        ? 'Online'
                        : `Seen ${relativeLastSeen(otherMember?.last_seen ?? null)}`}
                  </span>
                </span>
                <span className="text-[11px] text-slate-600">
                  {formatTimestamp(conversation.last_message_at || conversation.updated_at)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
